import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Undo as RetrieveIcon,
  CheckCircle as DonatedIcon,
  VolunteerActivism as DonateIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import Layout from '../components/Layout';
import letGoService from '../services/letGoService';
import { getBackendBaseUrl } from '../utils/configUtils';

const ExitBin = () => {
  const navigate = useAppNavigation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState({
    hide_exit_bin_from_search: false,
    let_go_email_reminders: true
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, item: null, action: null });

  const fetchExitBin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await letGoService.getExitBin();
      setItems(data.items || []);
    } catch (err) {
      console.error('Error fetching exit bin:', err);
      setError('Failed to load exit bin. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const data = await letGoService.getPreferences();
      setPreferences(data);
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  }, []);

  useEffect(() => {
    fetchExitBin();
    fetchPreferences();
  }, [fetchExitBin, fetchPreferences]);

  const handleToggleVisibility = async () => {
    try {
      const newValue = !preferences.hide_exit_bin_from_search;
      await letGoService.updatePreferences({ hide_exit_bin_from_search: newValue });
      setPreferences(prev => ({ ...prev, hide_exit_bin_from_search: newValue }));
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError('Failed to update preference.');
    }
  };

  const handleRetrieve = async (item) => {
    try {
      setActionLoading(item.id);
      await letGoService.retrieveFromExitBin(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Error retrieving item:', err);
      setError('Failed to retrieve item.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmDonation = async (item) => {
    try {
      setActionLoading(item.id);
      await letGoService.confirmDonation(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setConfirmDialog({ open: false, item: null, action: null });
    } catch (err) {
      console.error('Error confirming donation:', err);
      setError('Failed to confirm donation.');
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmDialog = (item, action) => {
    setConfirmDialog({ open: true, item, action });
  };

  const getImageUrl = (item) => {
    if (!item?.image_url) return '/placeholder.png';
    // Always use backend proxy for consistent image loading
    // GCS URLs may not be publicly accessible
    return `${getBackendBaseUrl()}/api/v2/files/item/${item.id}/${item.user_id || 0}`;
  };

  const getDaysMessage = (item) => {
    if (item.reminder_due) {
      return { text: 'Ready to donate!', color: 'warning.main' };
    }
    if (item.days_until_reminder === 0) {
      return { text: 'Reminder due today', color: 'warning.main' };
    }
    return { 
      text: `${item.days_until_reminder} days until reminder`, 
      color: 'text.secondary' 
    };
  };

  if (loading) {
    return (
      <Layout title="Exit Bin">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Header action button
  const headerActions = (
    <IconButton onClick={fetchExitBin} size="small">
      <RefreshIcon />
    </IconButton>
  );

  return (
    <Layout title="Exit Bin" action={headerActions}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, pb: 4 }}>
        {/* Description */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Items here are waiting to be donated. You can retrieve them anytime or confirm when donated.
          After 30 days, you'll get a reminder.
        </Alert>

        {/* Visibility Toggle */}
        <Card sx={{ mb: 3, p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.hide_exit_bin_from_search}
                onChange={handleToggleVisibility}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {preferences.hide_exit_bin_from_search ? <HiddenIcon /> : <VisibleIcon />}
                <Typography>
                  {preferences.hide_exit_bin_from_search 
                    ? 'Exit bin items hidden from search' 
                    : 'Exit bin items visible in search'}
                </Typography>
              </Box>
            }
          />
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {items.length === 0 && (
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <DonateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Exit Bin is Empty
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Items you mark for donation will appear here.
            </Typography>
            <Button variant="contained" onClick={() => navigate.to.LET_GO()}>
              Review Items
            </Button>
          </Card>
        )}

        {/* Items Grid */}
        <Grid container spacing={2}>
          {items.map((item) => {
            const daysInfo = getDaysMessage(item);
            const isLoading = actionLoading === item.id;

            return (
              <Grid item xs={12} sm={6} key={item.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  opacity: isLoading ? 0.7 : 1,
                  transition: 'opacity 0.2s'
                }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={getImageUrl(item)}
                    alt={item.name}
                    sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                    onError={(e) => { e.target.src = '/placeholder.png'; }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {item.name}
                    </Typography>
                    
                    {item.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {item.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip 
                        label={`${item.days_in_exit_bin} days`} 
                        size="small" 
                        variant="outlined"
                      />
                      <Typography variant="caption" color={daysInfo.color}>
                        {daysInfo.text}
                      </Typography>
                    </Box>

                    {item.reminder_due && !item.reminder_sent && (
                      <Alert severity="warning" sx={{ py: 0, mb: 1 }}>
                        <Typography variant="caption">
                          Consider donating this item!
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>

                  <Divider />

                  <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={isLoading ? <CircularProgress size={16} /> : <RetrieveIcon />}
                      onClick={() => handleRetrieve(item)}
                      disabled={isLoading}
                      sx={{ flex: 1 }}
                    >
                      Retrieve
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={isLoading ? <CircularProgress size={16} /> : <DonatedIcon />}
                      onClick={() => openConfirmDialog(item, 'donate')}
                      disabled={isLoading}
                      sx={{ flex: 1 }}
                    >
                      Donated
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Stats Footer */}
        {items.length > 0 && (
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 2,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {items.length} item{items.length !== 1 ? 's' : ''} waiting to be donated
            </Typography>
            {items.filter(i => i.reminder_due).length > 0 && (
              <Typography variant="body2" color="warning.main">
                {items.filter(i => i.reminder_due).length} ready for donation
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ open: false, item: null, action: null })}
      >
        <DialogTitle>Confirm Donation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark "{confirmDialog.item?.name}" as donated?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will remove the item from your inventory.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, item: null, action: null })}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={() => handleConfirmDonation(confirmDialog.item)}
            disabled={actionLoading === confirmDialog.item?.id}
          >
            {actionLoading === confirmDialog.item?.id ? (
              <CircularProgress size={20} />
            ) : (
              'Confirm Donated'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ExitBin;
